// Clinical-task API fns — carved verbatim from `src/api/scribeAPIV2.ts`
// (which now re-exports from here) as D1 of `monorepo-tasks-data-core`
// (ARCFE-937). Transport comes from the `@heidi/api` configured-client
// registry (E5) — `http()` returns the app's real `scribeHttpV2` singleton,
// so request behaviour is byte-identical, including the AIP-style
// `:assign`/`:unassign` custom-method routes, the `AssigneeType.user`
// default, the injected `ClinicalTaskStatus.todo` on create, and
// `getClinicalTasks`' snake_case param mapping.
//
// Deliberately NOT here: `generateSessionClinicalTasks` (Session-owned per
// the P0.4 ruling — retagged `@domain session` in the hub) and
// `deleteAllSessionClinicalTasks` (zero callers; deleted, not moved).
import { getScribeHttpClients } from '@heidi/api/client';
import { AssigneeType, ClinicalTaskStatus } from '@heidi/schema/ml-scribe';
import { formatToDateOnly } from '@heidi/utils/dates';
import { format } from 'date-fns';

import {
  CLINICAL_TASK_WORK_PROJECT_LINK_TYPE,
  type BulkClinicalTasksResponse,
  type ClinicalTaskLinkType,
  type ClinicalTaskResponse,
  type ClinicalTasksQueryParams,
  type CreateClinicalTaskParams,
  type GetClinicalTasksResponse,
  type UpdateClinicalTaskRequest,
} from '../../contracts';

/**
 * Narrow structural view of the app's axios client — the package's public fn
 * signatures never name axios types (declaration portability).
 */
interface TasksHttpClient {
  get<T>(
    url: string,
    config?: { params?: Record<string, unknown>; timeout?: number }
  ): Promise<{ data: T }>;
  post<T = unknown>(
    url: string,
    body?: unknown,
    config?: { timeout?: number }
  ): Promise<{ data: T }>;
  put<T = unknown>(
    url: string,
    body?: unknown,
    config?: { timeout?: number }
  ): Promise<{ data: T }>;
  patch<T = unknown>(
    url: string,
    body?: unknown,
    config?: { timeout?: number }
  ): Promise<{ data: T }>;
  delete<T = unknown>(
    url: string,
    config?: { params?: Record<string, unknown>; data?: unknown }
  ): Promise<{ data: T }>;
}

const http = (): TasksHttpClient => getScribeHttpClients().scribeHttpV2;

/** @domain tasks */
export const getClinicalTasks = (params: ClinicalTasksQueryParams = {}) => {
  const queryParams: Record<string, unknown> = {};
  if (params.sessionId) queryParams.session_id = params.sessionId;
  if (params.link) {
    queryParams.link_type = params.link.type;
    queryParams.link_id = params.link.id;
  }
  if (params.searchTerm) queryParams.search = params.searchTerm;
  if (params.status) queryParams.status = params.status;
  if (params.category) queryParams.category = params.category;
  if (params.dateFrom) queryParams.date_from = params.dateFrom.toISOString();
  if (params.dateTo) queryParams.date_to = params.dateTo.toISOString();
  if (params.dueDateFrom)
    queryParams.due_date_from = formatToDateOnly(params.dueDateFrom);
  if (params.dueDateTo) queryParams.due_date_to = formatToDateOnly(params.dueDateTo);
  if (params.sortBy) queryParams.sort_by = params.sortBy;
  if (params.sortOrder) queryParams.sort_order = params.sortOrder;
  if (params.skip !== undefined) queryParams.skip = params.skip;
  if (params.limit !== undefined) queryParams.limit = params.limit;
  if (params.assignmentFilter) queryParams.assignment_filter = params.assignmentFilter;

  return http().get<GetClinicalTasksResponse>(`/clinical-tasks`, {
    params: queryParams,
  });
};

/** @domain tasks */
export const getClinicalTaskById = (taskId: string) => {
  return http().get<ClinicalTaskResponse>(`/clinical-tasks/${taskId}`);
};

/** @domain tasks */
export const createClinicalTask = ({
  sessionId,
  title,
  category,
  description,
  dueDate,
  assigneeId,
  assigneeType,
  links,
}: CreateClinicalTaskParams) => {
  return http().post<ClinicalTaskResponse>(`/clinical-tasks`, {
    session_id: sessionId,
    status: ClinicalTaskStatus.todo,
    category,
    title,
    description,
    due_date: dueDate ? format(dueDate, 'yyyy-MM-dd') : undefined,
    assignee_id: assigneeId ?? undefined,
    assignee_type: assigneeType ?? undefined,
    links,
  });
};

/** @domain tasks */
export const updateClinicalTask = (
  taskId: string,
  taskData: UpdateClinicalTaskRequest
) => {
  return http().put<ClinicalTaskResponse>(`/clinical-tasks/${taskId}`, taskData);
};

/** @domain tasks */
export const deleteClinicalTask = (taskId: string) => {
  return http().delete(`/clinical-tasks/${taskId}`);
};

/** @domain tasks */
export const bulkUpdateClinicalTasks = (data: {
  ids: string[];
  status: ClinicalTaskStatus;
}) => {
  return http().patch<BulkClinicalTasksResponse>('/clinical-tasks/bulk', data);
};

/** @domain tasks */
export const bulkDeleteClinicalTasks = (data: { ids: string[] }) => {
  return http().delete<BulkClinicalTasksResponse>('/clinical-tasks/bulk', {
    data,
  });
};

/** @domain tasks */
export const assignClinicalTask = (
  taskId: string,
  assigneeId: string,
  assigneeType: AssigneeType = AssigneeType.user
) => {
  return http().post<ClinicalTaskResponse>(`/clinical-tasks/${taskId}:assign`, {
    assignee_id: assigneeId,
    assignee_type: assigneeType,
  });
};

/** @domain tasks */
export const unassignClinicalTask = (taskId: string) => {
  return http().post<ClinicalTaskResponse>(`/clinical-tasks/${taskId}:unassign`);
};

/** Backend already accepts WORK_PROJECT; the generated FE enum is stale. */
export type ClinicalTaskLinkWrite = {
  type: ClinicalTaskLinkType | 'WORK_PROJECT';
  id: string;
};

/** @domain tasks */
export const linkClinicalTask = (taskId: string, link: ClinicalTaskLinkWrite) => {
  return http().post<ClinicalTaskResponse>(`/clinical-tasks/${taskId}:link`, { link });
};

/** @domain tasks */
export const unlinkClinicalTask = (taskId: string, link: ClinicalTaskLinkWrite) => {
  return http().post<ClinicalTaskResponse>(`/clinical-tasks/${taskId}:unlink`, { link });
};

export const markClinicalTaskDelegatedToHeidi = (taskId: string, projectId: string) =>
  linkClinicalTask(taskId, {
    type: CLINICAL_TASK_WORK_PROJECT_LINK_TYPE,
    id: projectId,
  });

/** @domain tasks */
export const markClinicalTaskInProgress = (taskId: string) =>
  updateClinicalTask(taskId, { status: ClinicalTaskStatus.in_progress });
